import { itx } from "../../../../src/translationItx/itx";
import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { RoomType } from "../../types/C1_ModelTypes";
import { C1_FurnitureSvg } from "../C1_FurnitureSvg";
import { C1_FurnitureIcon } from "../C1_FurnitureIcon";

const DEFAULT_WIDTH = 1.6;
const DEFAULT_HEIGHT = 0.8;
const CHAIR_WIDTH = 0.5;
const CHAIR_DEPTH = 0.2;
const CHAIR_GAP = 0.06;

const DiningTable6: C1_FurnitureDefinition = {
  id: "diningTable6",
  icon: <C1_FurnitureIcon kind="dining-table-6" />,
  description: { en: "Dining table", de: "Esstisch" },
  category: RoomType.Dining,
  defaultWidth: DEFAULT_WIDTH,
  defaultHeight: DEFAULT_HEIGHT,
  grabsW: true,
  grabsH: true,
  originXPercentage: 50,
  originYPercentage: 50,
  properties: [
    {
      key: "numberOfChairs",
      get label() { return itx("c1.ui.Anzahl_der_Stuhle", "Anzahl der Stühle"); },
      type: "number",
      defaultValue: 6,
      min: 2,
      max: 12,
      step: 2,
    },
    {
      key: "chairRight",
      get label() { return itx("c1.ui.Stuhl_rechts", "Stuhl rechts"); },
      type: "boolean",
      defaultValue: true,
    },
    {
      key: "chairLeft",
      get label() { return itx("c1.ui.Stuhl_links", "Stuhl links"); },
      type: "boolean",
      defaultValue: true,
    },
  ],
  Renderer: ({
    width,
    height,
    rotation,
    PX,
    strokeWidth,
    strokeColor,
    fillColor,
    showDetails,
    numberOfChairs = 6,
    chairRight = true,
    chairLeft = true,
  }: C1_FurnitureRendererProps) => {
    const finalRotation = rotation ?? 0;
    const finalStrokeWidth = strokeWidth ?? 2;
    const finalStrokeColor = strokeColor ?? "#000000";
    const finalFillColor = fillColor ?? "#ffffff";
    const chairWidth = CHAIR_WIDTH * PX;
    const chairDepth = CHAIR_DEPTH * PX;
    const chairGap = CHAIR_GAP * PX;
    const tableWidth = Math.max(0.2 * PX, width);
    const tableHeight = Math.max(0.2 * PX, height);
    const totalChairs = Math.max(2, Math.min(12, Math.round(Number(numberOfChairs)) || 6));
    let remainingChairs = totalChairs;
    const showLeftChair = Boolean(chairLeft) && remainingChairs-- > 0;
    const showRightChair = Boolean(chairRight) && remainingChairs-- > 0;
    remainingChairs = Math.max(0, remainingChairs);
    const topChairCount = Math.ceil(remainingChairs / 2);
    const bottomChairCount = Math.floor(remainingChairs / 2);
    const horizontalChairCount = showDetails ? Math.max(topChairCount, bottomChairCount) : 0;
    const horizontalChairOverflow = horizontalChairCount
      ? Math.max(0, (chairWidth - tableWidth / horizontalChairCount) / 2)
      : 0;
    const verticalChairOverflow =
      showDetails && (showLeftChair || showRightChair) ? Math.max(0, (chairWidth - tableHeight) / 2) : 0;
    const leftExtension = Math.max(showDetails && showLeftChair ? chairGap + chairDepth : 0, horizontalChairOverflow);
    const rightExtension = Math.max(showDetails && showRightChair ? chairGap + chairDepth : 0, horizontalChairOverflow);
    const topExtension = Math.max(showDetails && topChairCount ? chairGap + chairDepth : 0, verticalChairOverflow);
    const bottomExtension = Math.max(
      showDetails && bottomChairCount ? chairGap + chairDepth : 0,
      verticalChairOverflow,
    );
    const boundingWidth = leftExtension + tableWidth + rightExtension;
    const boundingHeight = topExtension + tableHeight + bottomExtension;

    const horizontalChairs = (count: number, y: number) =>
      Array.from({ length: count }, (_, index) => {
        const segmentWidth = tableWidth / count;
        const x = segmentWidth * (index + 0.5) - chairWidth / 2;
        return (
          <rect
            key={`${y}_${index}`}
            x={x}
            y={y}
            width={chairWidth}
            height={chairDepth}
            rx={4}
            fill={finalFillColor}
            stroke={finalStrokeColor}
            strokeWidth={finalStrokeWidth}
          />
        );
      });

    return (
      <C1_FurnitureSvg
        PX={PX}
        width={boundingWidth}
        height={boundingHeight}
        rotation={finalRotation}
        viewBoxX={-leftExtension}
        viewBoxY={-topExtension}
        viewBoxWidth={boundingWidth}
        viewBoxHeight={boundingHeight}
        preserveAspectRatio="none"
        originXPercentage={50}
        originYPercentage={50}
      >
        <rect
          x={0}
          y={0}
          width={tableWidth}
          height={tableHeight}
          rx={4}
          fill={finalFillColor}
          stroke={finalStrokeColor}
          strokeWidth={finalStrokeWidth * 2}
        />
        {showDetails && (
          <>
            {horizontalChairs(topChairCount, -chairGap - chairDepth)}
            {horizontalChairs(bottomChairCount, tableHeight + chairGap)}
            {showLeftChair && (
              <rect
                x={-chairGap - chairDepth}
                y={tableHeight / 2 - chairWidth / 2}
                width={chairDepth}
                height={chairWidth}
                rx={4}
                fill={finalFillColor}
                stroke={finalStrokeColor}
                strokeWidth={finalStrokeWidth}
              />
            )}
            {showRightChair && (
              <rect
                x={tableWidth + chairGap}
                y={tableHeight / 2 - chairWidth / 2}
                width={chairDepth}
                height={chairWidth}
                rx={4}
                fill={finalFillColor}
                stroke={finalStrokeColor}
                strokeWidth={finalStrokeWidth}
              />
            )}
          </>
        )}
      </C1_FurnitureSvg>
    );
  },
};

export default DiningTable6;
